import { css } from "@emotion/css";
import { RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import type { EphemeralEnvironmentsOnboardingRunbook, GetEphemeralEnvironmentsOnboardingDataBffResponse } from "@octopusdeploy/octopus-server-client";
import React from "react";
import { Note, Select, Text } from "~/components/form";
import { ConfigWizardHelpLayout } from "./ConfigWizardHelpLayout";
import { ConfigWizardStatusBar } from "./ConfigWizardStatusBar";
import type { RunbooksInputMode, RunbookConfig } from "./EphemeralEnvironmentsConfigWizard";
import deprovisioningRunbookInfoImage from "./environment-deprovisioning-runbook-info.svg";
import provisioningRunbookInfoImage from "./environment-provisioning-runbook-info.svg";
export function ProvisioningRunbookHelp() {
    return (<ConfigWizardHelpLayout title="Provisioning runbook">
            A provisioning runbook automatically spins up the required infrastructure when an ephemeral environment is first created — whether the release is triggered by a pull request, a manual action, a GitHub workflow, or an API call. This step
            is optional.
        </ConfigWizardHelpLayout>);
}
export const ProvisioningRunbookHelpImage = {
    src: provisioningRunbookInfoImage,
    altText: "Graphic with branch 1 and branch 2 environment 1 and environment 2 using the provisioning runbook",
};
export function DeprovisioningRunbookHelp() {
    return (<ConfigWizardHelpLayout title="Deprovisioning runbook">
            A deprovisioning runbook automatically tears down an ephemeral environment when you no longer need it — for example, after a pull request is merged or closed. This prevents unused infrastructure from incurring cost.
        </ConfigWizardHelpLayout>);
}
export const DeprovisioningRunbookHelpImage = {
    src: deprovisioningRunbookInfoImage,
    altText: "Graphic with 3 environments. A 3 day old environment is deprovisioning and 2 1 day old environments are still active.",
};
interface RunbookContentProps {
    data: GetEphemeralEnvironmentsOnboardingDataBffResponse;
    runbookType: "Provisioning" | "Deprovisioning";
    runbookConfig: RunbookConfig;
    setRunbookConfig: (config: RunbookConfig) => void;
    isCommittingRunbooksToNewBranch: boolean;
}
export function RunbookContent({ data, runbookType, runbookConfig, setRunbookConfig, isCommittingRunbooksToNewBranch }: RunbookContentProps) {
    const styles = {
        inputsWidth: css({
            maxWidth: 400,
            width: "100%",
        }),
    };
    function handleRadioChange(value: RunbooksInputMode) {
        setRunbookConfig({ id: undefined, name: undefined, action: value });
    }
    const runbooks = data.Runbooks;
    const canConfigure = data.Permissions.ConfigureRunbooks;
    const canCreate = data.Permissions.CreateRunbooks;
    const canView = data.Permissions.ViewRunbooks;
    const noExistingRunbooks = runbooks.length === 0;
    return (<div>
            <ConfigWizardStatusBar activeStage={runbookType} isCommittingRunbooksToNewBranch={isCommittingRunbooksToNewBranch}/>
            <h3>{runbookType} Runbook</h3>
            {!canConfigure ? (<>
                    <p>You need permission to modify the project to configure a {runbookType.toLowerCase()} runbook.</p>
                    <p>Click Next to continue and configure a runbook later.</p>
                </>) : (<RadioButtonGroup value={runbookConfig.action} onChange={(value) => handleRadioChange(value)}>
                    <RadioButton key="select" label="Select existing" value="Select" disabled={!canView || noExistingRunbooks}/>
                    {runbookConfig.action == "Select" && (<div className={styles.inputsWidth}>
                            <SelectRunbookCombo runbooks={runbooks} runbookConfig={runbookConfig} setRunbookConfig={setRunbookConfig}/>
                        </div>)}
                    {!canView && <Note>You do not have permission to view runbooks</Note>}
                    {canView && noExistingRunbooks && <Note>There are no available runbooks</Note>}

                    <RadioButton key="create" label={`Add new`} value="Create" disabled={!canCreate}/>
                    {runbookConfig.action == "Create" && (<div className={styles.inputsWidth}>
                            <Text label={`Runbook name`} value={runbookConfig.name || ""} onChange={(value) => setRunbookConfig({ name: value, id: undefined, action: "Create" })}/>
                        </div>)}
                    <Note>{canCreate ? "Create a new runbook" : "You do not have permission to create runbooks"}</Note>

                    <RadioButton key="skip" label={`Skip for now`} value="Skip"/>
                    <Note>A {runbookType.toLowerCase()} runbook can be configured later</Note>
                </RadioButtonGroup>)}
        </div>);
}
interface SelectRunbookComboProps {
    runbooks: EphemeralEnvironmentsOnboardingRunbook[];
    runbookConfig: RunbookConfig;
    setRunbookConfig: (config: RunbookConfig) => void;
}
function SelectRunbookCombo({ runbooks, runbookConfig, setRunbookConfig }: SelectRunbookComboProps) {
    return (<Select label="Runbook name" value={runbookConfig?.id || ""} items={runbooks.map((runbook) => ({ value: runbook.Id, text: runbook.Name }))} onChange={(id) => {
            if (!id) {
                setRunbookConfig({ id: undefined, name: undefined, action: "Select" });
                return;
            }
            const matches = runbooks.filter((runbook) => runbook.Id === id);
            if (matches.length !== 1) {
                throw new Error(`Expected exactly one runbook with Id=${id}, but found ${matches.length}`);
            }
            setRunbookConfig({ id: id, name: matches[0].Name, action: "Select" });
        }}/>);
}
